import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import TextField from '@app/components/input/TextField';
import useValidationMessage from '@app/hooks/useValidationMessage';

import type { AddressFormNamespaces } from '../Address.constants';
import { AddressFormFields } from '../Address.constants';
import messages from '../Address.messages';
import AddressAutoSuggest from './AddressAutoSuggest';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

export interface AddressFieldsProps {
  namespace: AddressFormNamespaces;
}

export const AddressFields: FC<AddressFieldsProps> = ({ namespace }) => {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const { msg } = useValidationMessage(messages);

  return (
    <>
      <AddressAutoSuggest namespace={namespace} />

      <TextField
        {...register(`${namespace}.${AddressFormFields.StreetLine2}`)}
        data-testid={`${namespace}-addressLine2`}
        autoComplete="address-line2"
        maxLength={35}
        formLabel={
          <>
            <FormattedMessage {...messages.addressLineTwo} /> (
            <FormattedMessage {...messages.optional} />)
          </>
        }
      />

      <TextField
        {...register(`${namespace}.${AddressFormFields.Postcode}`, {
          required: {
            value: true,
            message: msg({
              type: 'required',
              key: 'postalCode',
            }),
          },
        })}
        data-testid={`${namespace}-zip`}
        autoComplete="postal-code"
        maxLength={60}
        isValid={!errors[namespace]?.[AddressFormFields.Postcode]}
        formLabel={<FormattedMessage {...messages.postalCode} />}
        half
      />

      <TextField
        {...register(`${namespace}.${AddressFormFields.City}`, {
          required: {
            value: true,
            message: msg({
              type: 'required',
              key: 'city',
            }),
          },
        })}
        data-testid={`${namespace}-city`}
        autoComplete="address-level2"
        maxLength={128}
        isValid={!errors[namespace]?.[AddressFormFields.City]}
        formLabel={<FormattedMessage {...messages.city} />}
      />
    </>
  );
};
